/**
 * Blog article table of contents.
 *
 * Three jobs, all of them things CSS alone cannot do:
 *
 *  1. keep `--clp-toc-top` in step with the header's measured bottom edge, so
 *     the summary sits just under the header while it is on screen and moves
 *     back up once it has gone (the theme exposes no header-height variable,
 *     and the height changes with the breakpoint);
 *  2. highlight the section being read — every other entry is dimmed;
 *  3. drive the mobile <select>, which replaces the list on small screens.
 */
const SELECT = "[data-article-toc-select]";
const NAV = ".c-article-toc";
const HEADINGS = ".c-content-single__content h2[id]";

/**
 * Keep the summary clear of the page header.
 *
 * The header is fixed and slides in and out as the reader scrolls, so the
 * offset follows its *measured* bottom edge rather than a scroll-direction
 * attribute: 10px under the header while it is on screen, 10px from the top
 * once it has gone. The nav's max-height uses the same variable, so it is
 * always exactly the space that is left.
 */
const GAP = 10;

function followHeader(rail) {
  const header = document.querySelector("header.c-page-header");
  if (!header) return;

  let queued = false;

  const apply = () => {
    queued = false;
    const bottom = header.getBoundingClientRect().bottom;
    const top = bottom > 0 ? Math.round(bottom) + GAP : GAP;
    rail.style.setProperty("--clp-toc-top", `${top}px`);
  };

  const schedule = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(apply);
  };

  apply();
  window.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", schedule);
}

function scrollToSection(hash) {
  const target = document.getElementById(hash.replace(/^#/, ""));
  if (!target) return;

  // The window is scrolled explicitly rather than through scrollIntoView():
  // an ancestor of the article carries its own overflow, which swallows
  // scrollIntoView and leaves the page where it was. The offset is read from
  // the heading's scroll-margin-top so the sticky bar clearance stays defined
  // in one place, the stylesheet.
  const offset = parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
  const top = target.getBoundingClientRect().top + window.scrollY - offset;

  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });

  // Keep the URL in step without triggering a second, jumpy native scroll.
  if (window.history && window.history.replaceState) {
    window.history.replaceState(null, "", hash);
  }
}

/**
 * Dim every entry but the one whose section is currently being read.
 */
function trackActiveSection(nav) {
  const headings = [...document.querySelectorAll(HEADINGS)];
  if (!headings.length) return;

  const items = new Map();
  nav.querySelectorAll(".c-article-toc__link").forEach((link) => {
    const id = (link.getAttribute("href") || "").replace(/^#/, "");
    const item = link.closest(".c-article-toc__item");
    if (id && item) items.set(id, item);
  });
  if (!items.size) return;

  let queued = false;

  const update = () => {
    queued = false;

    // The heading closest above the reading line wins; before the first one,
    // the first entry stays highlighted.
    const header = document.querySelector("header.c-page-header");
    const line = (header ? Math.max(0, header.getBoundingClientRect().bottom) : 0) + 40;

    let current = headings[0].id;
    headings.forEach((heading) => {
      if (heading.getBoundingClientRect().top <= line) current = heading.id;
    });

    items.forEach((item, id) => {
      item.classList.toggle("is-active", id === current);
    });
  };

  const onScroll = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(update);
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  update();
}

function init() {
  document.querySelectorAll(".c-article-toc-rail").forEach(followHeader);

  document.querySelectorAll(SELECT).forEach((select) => {
    select.addEventListener("change", () => {
      const hash = select.value;
      if (!hash) return;

      scrollToSection(hash);
      // Reset to the label so the control reads as a menu, not a state.
      select.selectedIndex = 0;
    });
  });

  document.querySelectorAll(NAV).forEach(trackActiveSection);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
